import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import trioLogo from '@/assets/trioLogo.png';
import useProfile from '@/hooks/useProfileV2';
import { supabase } from '@/lib/supabase';
import * as Styles from '../SignIn/SignIn.styles';

const OnboardingPage = function (): JSX.Element {
  const navigate = useNavigate();
  const { profile, refresh } = useProfile();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (!form.firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    if (!form.password || form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      // Set the password on the auth account
      const { error: passError } = await supabase.auth.updateUser({
        password: form.password,
      });
      if (passError) {
        setError(passError.message);
        setIsLoading(false);
        return;
      }

      // Update the profile with name
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
        })
        .eq('id', profile.id);

      if (profileError) {
        setError(profileError.message);
        setIsLoading(false);
        return;
      }

      // Refresh profile context and redirect
      refresh();
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  }, [form, profile.id, refresh, navigate]);

  return (
    <Styles.Grid>
      <Styles.ContentContainer>
        <Styles.Title>Welcome</Styles.Title>
        <Styles.SubTitle>
          Let&apos;s set up your account so you can get started.
        </Styles.SubTitle>
      </Styles.ContentContainer>

      <Styles.SignInContainer>
        <Styles.Card>
          <Styles.Img src={trioLogo} alt="Trio's logo" />
          <Styles.FormTitle>Set up your account</Styles.FormTitle>
          <Styles.Paragraph>
            {profile.email}
          </Styles.Paragraph>

          {error && (
            <Styles.ErrorMessageBox type="error">
              {error}
            </Styles.ErrorMessageBox>
          )}

          <Styles.Form>
            <Styles.Input
              type="text"
              name="firstName"
              placeholder="First name"
              value={form.firstName}
              onChange={handleChange}
              required
            />

            <Styles.Input
              type="text"
              name="lastName"
              placeholder="Last name"
              value={form.lastName}
              onChange={handleChange}
            />

            <Styles.Input
              type="password"
              name="password"
              placeholder="Create a password"
              value={form.password}
              onChange={handleChange}
              required
            />

            <Styles.Input
              type="password"
              name="confirmPassword"
              placeholder="Confirm password"
              value={form.confirmPassword}
              onChange={handleChange}
              required
            />

            <Styles.SignInBtn
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Setting up...' : 'GET STARTED'}
            </Styles.SignInBtn>
          </Styles.Form>
        </Styles.Card>
      </Styles.SignInContainer>
    </Styles.Grid>
  );
};

export default OnboardingPage;
